export const CTA_STYLE_OPTIONS = ["primary", "secondary", "outline"];
export const CTA_PLACEMENT_OPTIONS = ["hero", "body", "sidebar", "sunset"];

export const CTA_PRESET_DEFINITIONS = [
  {
    key: "register",
    label: "Register",
    url: "/signup",
    helper_text: "Send visitors to a registration or signup page.",
    style: "primary",
    placement: "hero",
  },
  {
    key: "donate",
    label: "Donate",
    url: "https://www.roadhomeprogram.org/donate",
    helper_text: "Highlight giving options for supporters.",
    style: "secondary",
    placement: "sidebar",
  },
  {
    key: "view-flyer",
    label: "View Flyer",
    url: "/",
    helper_text: "Link to a hosted PDF or event flyer.",
    style: "outline",
    placement: "body",
  },
  {
    key: "get-directions",
    label: "Get Directions",
    url: "/",
    helper_text: "Link to maps, parking guidance, or venue directions.",
    style: "secondary",
    placement: "sidebar",
  },
  {
    key: "join-mailing-list",
    label: "Join Mailing List",
    url: "/signup",
    helper_text: "Capture interest when registration is closed or limited.",
    style: "primary",
    placement: "body",
  },
  {
    key: "contact-us",
    label: "Contact Us",
    url: "mailto:",
    helper_text: "Provide a direct email or support path.",
    style: "outline",
    placement: "sidebar",
  },
];

const CTA_URL_PATTERN = /^(https?:\/\/|mailto:|tel:|\/)/i;

function coerceString(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function coerceNonNegativeInteger(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.trunc(parsed);
}

function buildFallbackCtaId(index, label, placement) {
  const safeLabel = coerceString(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `cta-${placement || "body"}-${safeLabel || index + 1}`;
}

export function createCtaId() {
  return `cta-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createEmptyCtaConfig(overrides = {}) {
  return {
    id: createCtaId(),
    label: "",
    url: "",
    helper_text: "",
    style: "primary",
    placement: "body",
    enabled: false,
    sort_order: 0,
    ...overrides,
  };
}

export function isValidCtaUrl(url) {
  return CTA_URL_PATTERN.test(coerceString(url));
}

export function isExternalHttpUrl(url) {
  return /^https?:\/\//i.test(coerceString(url));
}

export function getCtaLinkProps(url) {
  return isExternalHttpUrl(url)
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};
}

export function normalizeCtaConfig(rawConfig) {
  if (!Array.isArray(rawConfig)) return [];

  return rawConfig
    .filter((item) => item && typeof item === "object")
    .map((item, index) => {
      const label = coerceString(item.label);
      const url = coerceString(item.url);
      const placement = CTA_PLACEMENT_OPTIONS.includes(coerceString(item.placement))
        ? coerceString(item.placement)
        : "body";
      const style = CTA_STYLE_OPTIONS.includes(coerceString(item.style))
        ? coerceString(item.style)
        : "primary";

      return {
        id: coerceString(item.id) || buildFallbackCtaId(index, label || url, placement),
        label,
        url,
        helper_text: coerceString(item.helper_text ?? item.helperText),
        style,
        placement,
        enabled: !!item.enabled,
        sort_order: coerceNonNegativeInteger(item.sort_order ?? item.sortOrder, index),
      };
    });
}

export function getSortedEnabledCtas(rawConfig, placement) {
  return normalizeCtaConfig(rawConfig)
    .filter((item) => item.enabled && item.placement === placement)
    .sort((a, b) => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.label.localeCompare(b.label);
    });
}

export function validateCtaConfig(rawConfig) {
  const ctas = normalizeCtaConfig(rawConfig);
  const itemErrors = {};

  ctas.forEach((cta) => {
    if (!cta.enabled) return;

    const errors = {};
    if (!cta.label) errors.label = "Enabled CTA label is required.";
    if (!cta.url) {
      errors.url = "Enabled CTA URL is required.";
    } else if (!isValidCtaUrl(cta.url)) {
      errors.url = "URL must start with http://, https://, mailto:, tel:, or /";
    }

    if (Object.keys(errors).length > 0) {
      itemErrors[cta.id] = errors;
    }
  });

  return {
    itemErrors,
    errorCount: Object.keys(itemErrors).length,
  };
}
